// src/components/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Mail, MapPin, Phone } from 'lucide-react';
import BrandMark from './BrandMark';
import { SITE, emailHref, mapHref, phoneHref } from '../siteInfo';

/**
 * The footer stays dark in both colour modes — it is the ground the page sits
 * on. Only the depth of the olive changes, so light mode gets a footer that
 * still looks lit and dark mode gets one that does not glow.
 */
const FIELD = {
  light: { top: '#4c5f27', bottom: '#333f1a' },
  dark: { top: '#333f1c', bottom: '#242c14' },
};

const CREAM = '#f4f1e6';
const CREAM_DIM = 'rgba(244,241,230,0.68)';
const RULE = 'rgba(244,241,230,0.14)';

/**
 * Only links a signed-out visitor can actually follow. /cart is behind
 * ProtectedRoute, so a shortcut to it bounces them to the login page instead
 * of the cart — the header's cart button opens the dropdown for everyone.
 */
const QUICK_LINKS = [
  { label: 'Home', to: '/home' },
  { label: 'Our products', to: '/home#products' },
  { label: 'Contact us', to: '/home#contact' },
  { label: 'Privacy notice', to: '/privacy' },
];

/** A column heading, with the short olive rule that ties the three together. */
const ColumnHeading = ({ children }) => (
  <Typography
    component="h3"
    sx={{
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.16em',
      textTransform: 'uppercase',
      color: CREAM_DIM,
      mb: 2,
      pb: 1,
      borderBottom: '2px solid',
      borderColor: 'rgba(179,199,127,0.45)',
      display: 'inline-block',
    }}
  >
    {children}
  </Typography>
);

const Footer = () => {
  const theme = useTheme();
  const field = theme.palette.mode === 'dark' ? FIELD.dark : FIELD.light;

  const toTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const linkSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    color: CREAM_DIM,
    textDecoration: 'none',
    fontSize: '0.92rem',
    transition: 'color .2s ease, transform .2s ease',
    '&:hover': { color: CREAM, transform: 'translateX(3px)' },
  };

  return (
    <Box component="footer" id="contact" sx={{ mt: 'auto', scrollMarginTop: '96px' }}>
      {/* Two rows of hills lift the footer out of the page instead of cutting
          it off with a straight edge. Drawn in the footer's own top colour so
          the seam between them never shows. */}
      <Box aria-hidden sx={{ lineHeight: 0, color: field.top }}>
        <Box
          component="svg"
          viewBox="0 0 1440 90"
          preserveAspectRatio="none"
          sx={{ display: 'block', width: '100%', height: { xs: 40, md: 68 } }}
        >
          <path
            d="M0,90 C160,50 300,30 470,44 C640,58 760,86 900,78 C1040,70 1180,34 1300,26 C1362,22 1410,28 1440,36 L1440,90 Z"
            fill="currentColor"
            opacity="0.38"
          />
          <path
            d="M0,90 C140,70 280,56 440,66 C620,77 760,98 920,88 C1080,78 1220,56 1340,52 C1392,50 1420,54 1440,58 L1440,90 Z"
            fill="currentColor"
          />
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(180deg, ${field.top} 0%, ${field.bottom} 100%)`,
          color: CREAM,
          // A faint diagonal weave, the way light falls across a baled field.
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            backgroundImage:
              'repeating-linear-gradient(118deg, rgba(255,255,255,0.045) 0 1px, transparent 1px 15px)',
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 5, md: 7 } }}>
          <Box
            sx={{
              display: 'grid',
              gap: { xs: 4.5, md: 5 },
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: '1.7fr 1fr 1.3fr',
              },
            }}
          >
            {/* At the two column width the brand takes the whole first row,
                so Explore and Get in touch sit side by side underneath it
                rather than one of them being orphaned. */}
            <Box sx={{ gridColumn: { sm: '1 / -1', md: 'auto' } }}>
              <BrandMark size="lg" tone="dark" />
              <Typography sx={{ mt: 2.5, maxWidth: 380, fontSize: '0.92rem', lineHeight: 1.75, color: CREAM_DIM }}>
                {SITE.about}
              </Typography>
            </Box>

            <Box>
              <ColumnHeading>Explore</ColumnHeading>
              <Stack spacing={1.5} alignItems="flex-start">
                {QUICK_LINKS.map((link) => (
                  <Box
                    key={link.label}
                    component={Link}
                    to={link.to}
                    onClick={link.to === '/home' ? toTop : undefined}
                    sx={linkSx}
                  >
                    {link.label}
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <ColumnHeading>Get in touch</ColumnHeading>
              <Stack spacing={2}>
                <Box
                  component="a"
                  href={mapHref}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ ...linkSx, alignItems: 'flex-start', '&:hover': { color: CREAM } }}
                >
                  <Box sx={{ pt: '3px', display: 'flex', color: 'rgba(179,199,127,0.9)' }}>
                    <MapPin size={17} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(244,241,230,0.45)' }}>
                      Address
                    </Typography>
                    <Typography sx={{ fontSize: '0.92rem', color: 'inherit' }}>{SITE.address}</Typography>
                  </Box>
                </Box>

                <Box component="a" href={phoneHref} sx={{ ...linkSx, alignItems: 'flex-start' }}>
                  <Box sx={{ pt: '3px', display: 'flex', color: 'rgba(179,199,127,0.9)' }}>
                    <Phone size={17} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(244,241,230,0.45)' }}>
                      Phone
                    </Typography>
                    <Typography sx={{ fontSize: '0.92rem', color: 'inherit' }}>{SITE.phone}</Typography>
                  </Box>
                </Box>

                <Box component="a" href={emailHref} sx={{ ...linkSx, alignItems: 'flex-start' }}>
                  <Box sx={{ pt: '3px', display: 'flex', color: 'rgba(179,199,127,0.9)' }}>
                    <Mail size={17} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(244,241,230,0.45)' }}>
                      Email
                    </Typography>
                    <Typography sx={{ fontSize: '0.92rem', color: 'inherit', wordBreak: 'break-all' }}>
                      {SITE.email}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider sx={{ my: { xs: 4, md: 5 }, borderColor: RULE }} />

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(244,241,230,0.5)' }}>
              &copy; {new Date().getFullYear()} {SITE.name}. All rights reserved.
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title="Back to top">
                <IconButton
                  onClick={toTop}
                  aria-label="Back to top"
                  size="small"
                  sx={{
                    color: CREAM_DIM,
                    border: '1px solid',
                    borderColor: RULE,
                    '&:hover': { color: CREAM, borderColor: 'rgba(179,199,127,0.6)' },
                  }}
                >
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
